import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { CircleChevronDown, EllipsisVertical, Share, SquarePlus, X } from 'lucide-react'
import { Button } from '@/ui/components/common/Button'
import { classNames } from '@/ui/utils/main'
import { useInstallPrompt } from '@/application/pwa/useInstallPrompt'
import { selectHasRespondedAndValid, useConsentStore } from '@/application/stores/consent.store'

/**
 * First-visit invitation to install the site as an app on the home screen.
 *
 * ## Why it is not a modal
 *
 * Like the cookie banner, this is a non-blocking `region`: a visitor who
 * landed on an article came to read it, and trapping focus in an install
 * advert before they can would be hostile. It never blocks pointer
 * interaction and disappears for good once closed.
 *
 * ## Placement
 *
 * On a phone the browser chrome the instructions point at is at the BOTTOM
 * of the screen (iOS Safari's tab bar holds the Share button), so the
 * invitation is a bottom sheet sitting right above it, padded past the home
 * indicator. On a tablet that chrome is a TOP toolbar, so it becomes a card
 * anchored under the navbar on the right — next to the button it talks
 * about, and out of the way of the page's text column.
 *
 * ## Ordering with the cookie banner
 *
 * Both are bottom-anchored on a phone, so this waits until the consent
 * decision has been made rather than stacking two sheets over each other.
 */
export function InstallAppPrompt() {
  const { t } = useTranslation()
  const { isVisible, platform, formFactor, canPromptNatively, promptInstall, dismiss } =
    useInstallPrompt()
  const hasAnsweredCookies = useConsentStore(selectHasRespondedAndValid)

  if (!isVisible || !hasAnsweredCookies) return null

  const steps: { icon: ReactNode; text: string }[] = []

  if (platform === 'android') {
    if (!canPromptNatively) {
      steps.push(
        {
          icon: <EllipsisVertical className="h-4 w-4" aria-hidden="true" />,
          text: t('pwa_install.android_step_menu'),
        },
        {
          icon: <SquarePlus className="h-4 w-4" aria-hidden="true" />,
          text: t('pwa_install.android_step_add'),
        },
      )
    }
  } else {
    steps.push(
      {
        icon: <Share className="h-4 w-4" aria-hidden="true" />,
        text:
          platform === 'ipados'
            ? t('pwa_install.ipados_step_share')
            : t('pwa_install.ios_step_share'),
      },
      {
        icon: <CircleChevronDown className="h-4 w-4" aria-hidden="true" />,
        text: t('pwa_install.ios_step_more'),
      },
      {
        icon: <SquarePlus className="h-4 w-4" aria-hidden="true" />,
        text: t('pwa_install.ios_step_add'),
      },
    )
  }

  return (
    <div
      role="region"
      aria-live="polite"
      aria-label={t('pwa_install.title')}
      className={classNames(
        'fixed z-40 border border-border bg-card shadow-lg motion-safe:animate-in motion-safe:duration-300',
        formFactor === 'phone'
          ? 'inset-x-0 bottom-0 rounded-t-lg border-x-0 border-b-0 pb-[env(safe-area-inset-bottom)] motion-safe:slide-in-from-bottom'
          : 'right-4 top-20 w-[22rem] max-w-[calc(100vw-2rem)] rounded-lg motion-safe:slide-in-from-right',
      )}
    >
      <div className="flex flex-col gap-3 p-4">
        <div className="flex items-start justify-between gap-3">
          <p className="text-sm font-medium text-foreground">{t('pwa_install.title')}</p>
          <button
            type="button"
            onClick={dismiss}
            aria-label={t('pwa_install.close')}
            className="-m-1 rounded-sm p-1 text-muted-foreground transition-opacity hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <p className="text-sm text-muted-foreground">{t('pwa_install.description')}</p>

        {steps.length > 0 && (
          <ol className="flex flex-col gap-2">
            {steps.map((step) => (
              <li key={step.text} className="flex items-center gap-2 text-sm text-foreground">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                  {step.icon}
                </span>
                <span>{step.text}</span>
              </li>
            ))}
          </ol>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={dismiss}>
            {t('pwa_install.dismiss')}
          </Button>
          {canPromptNatively && (
            <Button
              type="button"
              size="sm"
              onClick={() => {
                void promptInstall()
              }}
            >
              {t('pwa_install.install_action')}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
